import React, { ReactNode } from 'react';
import { cn } from '@/design-system/lib/utils';
import SideBarModal from './SideBarModal';

export type ModalIconTone =
  'primary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Remix icon class name (e.g. 'ri-checkbox-circle-line') OR a ReactNode.
   * When provided, renders inside a tinted square header chip.
   */
  icon?: string | ReactNode;
  iconTone?: ModalIconTone;
  /** Modal title (required when using the wrapper). */
  title: ReactNode;
  /** Optional subtitle / context line under the title. */
  subtitle?: ReactNode;
  /**
   * Right-aligned content in the header — useful for mode toggles,
   * meta badges, status pills, etc.
   */
  headerExtra?: ReactNode;
  /** Footer slot — typically Cancel + primary action buttons. */
  footer?: ReactNode;
  /** Card width — small / medium / large / extra large / full */
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Center sheet (default) or right drawer. */
  position?: 'center' | 'right';
  /** Confirm before close on backdrop / Escape (forms with unsaved data). */
  confirmOnClose?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  /** Pad the body? Default true. Set false for full-bleed bodies. */
  padded?: boolean;
  /** Extra classes for the modal card. */
  className?: string;
  /** Extra classes for the body wrapper. */
  bodyClassName?: string;
  /** Forwarded ref to the scrollable body. */
  contentRef?: React.Ref<HTMLDivElement>;
  /** Opened on top of another modal (see SideBarModal `stacked`). */
  stacked?: boolean;
  children: ReactNode;
}

const ICON_TONE_CLASSES: Record<ModalIconTone, string> = {
  primary: 'bg-(--theme-accent-alpha-10) text-accent',
  success: 'bg-(--state-success-lighter) text-(--state-success-dark)',
  warning: 'bg-(--state-warning-lighter) text-(--state-warning-dark)',
  danger: 'bg-(--state-error-lighter) text-(--state-error-dark)',
  info: 'bg-(--state-information-lighter) text-(--state-information-dark)',
  neutral: 'bg-bg-weak-50 text-text-sub-600',
};

const renderIcon = (icon: string | ReactNode, tone: ModalIconTone) => {
  const node =
    typeof icon === 'string' ? <i className={`${icon} text-[18px]`} /> : icon;
  return (
    <div
      className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
        ICON_TONE_CLASSES[tone]
      )}
    >
      {node}
    </div>
  );
};

/**
 * High-level modal with the standard icon-tinted header. All app modals
 * should use this wrapper so they stay visually consistent. For modals
 * that need a custom header layout, drop down to <SideBarModal> directly.
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  icon,
  iconTone = 'primary',
  title,
  subtitle,
  headerExtra,
  footer,
  width = 'md',
  position = 'center',
  confirmOnClose = false,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  padded = true,
  className = '',
  bodyClassName = '',
  contentRef,
  stacked = false,
  children,
}) => {
  const header = (
    <div className="flex items-start gap-3">
      {icon && renderIcon(icon, iconTone)}
      <div className="min-w-0 flex-1">
        <h3 className="text-base font-medium tracking-tight text-text-strong-950">
          {title}
        </h3>
        {subtitle && (
          <p className="mt-0.5 text-sm text-text-sub-600">{subtitle}</p>
        )}
      </div>
      {headerExtra && (
        <div className="flex shrink-0 items-center gap-2">{headerExtra}</div>
      )}
    </div>
  );

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      header={header}
      footer={footer}
      width={width}
      position={position}
      confirmOnClose={confirmOnClose}
      closeOnBackdropClick={closeOnBackdropClick}
      closeOnEscape={closeOnEscape}
      showCloseButton={showCloseButton}
      className={className}
      contentRef={contentRef}
      stacked={stacked}
    >
      <div className={`${padded ? 'px-5 py-4' : ''} ${bodyClassName}`.trim()}>
        {children}
      </div>
    </SideBarModal>
  );
};

export default Modal;
